import {
  Card,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { IListPrice } from "@/interfaces/list-price.interface";
import { ListPriceMessages } from "@/messages/listPrice/list-price.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { FILTER_OPTIONS, FILTER_OPTION_VALUES } from "../../constants";

type Props = {
  messages: ListPriceMessages;
  filters: IFilter<IListPrice>[];
  deleteFilter: (index: number) => void;
};

const filterFieldsConfig = {
  code: { filterType: FilterType.Contains, needsTrim: true },
  title: { filterType: FilterType.Contains, needsTrim: true },
  ean: { filterType: FilterType.Contains, needsTrim: true },
  active: { filterType: FilterType.Equals, needsTrim: false },
} as const;

const emptyFilterValues = {
  code: "",
  title: "",
  ean: "",
  active: "",
};

function ListPriceFiltersForm({ messages, filters, deleteFilter }: Props) {
  const { setValues, submitForm } = useFormikContext<IFilter<IListPrice>[]>();
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [currentFilterValues, setCurrentFilterValues] = useState(emptyFilterValues);

  const getFieldLabelByProperty = (property: string) => {
    const availableOptions = FILTER_OPTIONS(messages);
    return availableOptions.find((option) => option.value === property)?.label || property;
  };

  const updateSingleFilterValue = (
    fieldName: keyof typeof currentFilterValues,
    newValue: string
  ) => {
    setCurrentFilterValues((previousValues) => ({ ...previousValues, [fieldName]: newValue }));
  };

  const resetAllFiltersToEmpty = () => {
    setCurrentFilterValues(emptyFilterValues);
    setValues([]);
  };

  const applyFilledFilters = () => {
    const filtersWithValues = Object.entries(currentFilterValues)
      .filter(([, fieldValue]) => fieldValue.trim())
      .map(([fieldName, fieldValue]) => {
        const fieldConfig = filterFieldsConfig[fieldName as keyof typeof filterFieldsConfig];
        return {
          property: fieldName,
          value: fieldConfig.needsTrim ? fieldValue.trim() : fieldValue,
          filterType: fieldConfig.filterType,
          label: getFieldLabelByProperty(fieldName),
        };
      });

    setValues(filtersWithValues);
    submitForm();
  };

  const formatFilterValueForDisplay = (filter: IFilter<IListPrice>) => {
    if (filter.property === "active") {
      return filter.value === "true" ? messages.statusTrueOption : messages.statusFalseOption;
    }
    return filter.value;
  };

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={{ lg: 6, md: 4, sm: 6, xs: 12 }}>
            <TextField
              name="code"
              label={getFieldLabelByProperty("code")}
              value={currentFilterValues.code}
              onChange={(e) => updateSingleFilterValue("code", e.target.value)}
              fullWidth
              variant="outlined"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ lg: 6, md: 4, sm: 6, xs: 12 }}>
            <TextField
              name="title"
              label={getFieldLabelByProperty("title")}
              value={currentFilterValues.title}
              onChange={(e) => updateSingleFilterValue("title", e.target.value)}
              fullWidth
              variant="outlined"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={12}>
            <Collapse in={showMoreFilters} unmountOnExit>
              <Grid container spacing={2}>
                <Grid size={{ lg: 7, md: 4, sm: 6, xs: 12 }}>
                  <TextField
                    name="ean"
                    label={getFieldLabelByProperty("ean")}
                    value={currentFilterValues.ean}
                    onChange={(e) => updateSingleFilterValue("ean", e.target.value)}
                    fullWidth
                    variant="outlined"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>

                <Grid size={{ lg: 5, md: 4, sm: 6, xs: 12 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink>{getFieldLabelByProperty("active")}</InputLabel>
                    <Select
                      value={currentFilterValues.active}
                      onChange={(e) => updateSingleFilterValue("active", e.target.value)}
                      label={getFieldLabelByProperty("active")}
                      name="active"
                      notched
                    >
                      {FILTER_OPTION_VALUES(messages).active?.map((option, index) => (
                        <MenuItem value={String(option.value)} key={`${index}-${option.value}`}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>

            <Grid container spacing={2} mt={showMoreFilters ? 2 : 0}>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setShowMoreFilters(!showMoreFilters)}
                  fullWidth
                >
                  {showMoreFilters ? messages.lessFilters : messages.moreFilters}
                </MDButton>
              </Grid>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton
                  variant="gradient"
                  color="light"
                  onClick={resetAllFiltersToEmpty}
                  fullWidth
                >
                  {messages.clearFilters}
                </MDButton>
              </Grid>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton variant="gradient" color="dark" onClick={applyFilledFilters} fullWidth>
                  {messages.filter}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>

          {filters.length > 0 && (
            <Grid container size={12} gap={1} sx={{ mt: 2 }}>
              {filters.map((filter, index) => (
                <Grid key={index}>
                  <Chip
                    label={`${filter.label}: ${formatFilterValueForDisplay(filter)}`}
                    onDelete={() => deleteFilter(index)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </MDBox>
    </Card>
  );
}

export default ListPriceFiltersForm;
